import React from "react";
import { FaInfoCircle, FaRedo } from "react-icons/fa";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import ScrollIndicator from "../../Home/ScrollIndicator";
import useScrollToTop from "../../../hooks/useScollToTop";

import EditResultModal from "./components/EditResultModal";
import ResultatEventCard from "./components/ResultatEventCard";
import ResultatsEmptyState from "./components/ResultatsEmptyState";
import ResultatsHero from "./components/ResultatsHero";
import ResultatsImageModal from "./components/ResultatsImageModal";
import ResultatsSkeleton from "./components/ResultatsSkeleton";
import ResultatsStats from "./components/ResultatsStats";
import ResultatsToolbar from "./components/ResultatsToolbar";

import { useResultatsPage } from "./hooks/useResultatsPage";
import { getResultEventKey } from "./utils/resultatsUtils";
import AdherentEventSelector from "./../EventPage/components/AdherentEventSelector";

function ResultatsPage() {
  useScrollToTop();

  const {
    events,
    filteredEvents,
    filters,
    form,
    selectedEvent,
    selectedImage,
    isEditModalOpen,
    loading,
    refreshing,
    saving,
    deletingIds,
    addingPalmaresIds,
    palmaresStatus,
    activeFiltersCount,
    stats,
    fetchData,
    setSelectedImage,
    handleFilterChange,
    resetFilters,
    handleInputChange,
    handleEdit,
    handleCloseEditModal,
    handleSubmit,
    handleAddToPalmares,
    handleOpenLink,
    handleImageClick,
    handleUnregisterWithConfirm,
    adherents,
    selectedAdherentId,
    adherentsLoading,
    selectAdherent,
  } = useResultatsPage();

  return (
    <main className="min-h-screen bg-slate-50 pb-24 pt-[88px] text-slate-950 dark:bg-slate-950 dark:text-white lg:pt-[104px]">
      <ResultatsHero stats={stats} />

      <section className="mx-auto -mt-12 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-white/80 bg-white/95 p-4 shadow-2xl shadow-slate-200/70 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/95 dark:shadow-black/30 sm:p-6">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-2 text-xs font-bold uppercase tracking-[0.16em] text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">
                <FaInfoCircle />
                Historique sportif
              </div>

              <h1 className="mt-4 font-titre text-3xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-4xl">
                Résultats
              </h1>

              <p className="mt-3 max-w-3xl text-sm font-medium leading-7 text-slate-600 dark:text-white/60 sm:text-base">
                Retrouve tes compétitions, ajoute ou modifie tes résultats,
                associe une photo de podium ou un tableau, puis sauvegarde tes
                meilleures performances dans ton palmarès.
              </p>
            </div>

            <button
              type="button"
              onClick={fetchData}
              disabled={refreshing}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:border-amber-200 hover:text-amber-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-amber-300/40 dark:hover:text-amber-100 lg:min-w-[220px]"
            >
              <FaRedo className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Actualisation..." : "Actualiser"}
            </button>
          </div>

          <div className="mt-5 rounded-[1.5rem] border border-amber-100 bg-amber-50/80 p-4 dark:border-amber-300/10 dark:bg-amber-500/10">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-700 shadow-sm dark:bg-white/10 dark:text-amber-100">
                <FaInfoCircle />
              </div>

              <div>
                <p className="text-sm font-bold text-slate-950 dark:text-white">
                  Comment utiliser cette page ?
                </p>
                <p className="mt-1 text-sm font-medium leading-6 text-slate-600 dark:text-white/60">
                  Tu peux renseigner le déroulé de tes combats, ton classement,
                  tes observations, puis ajouter le résultat au palmarès. Les
                  liens podium et tableau peuvent être visualisés directement.
                </p>
              </div>
            </div>
          </div>

          <ResultatsStats stats={stats} />
        </div>
      </section>

      <section className="mx-auto mt-6 w-full max-w-[1280px] px-4 sm:px-6 lg:px-8">
        {!adherentsLoading && adherents.length > 0 && (
          <AdherentEventSelector
            adherents={adherents}
            selectedId={selectedAdherentId}
            onSelect={selectAdherent}
          />
        )}
        <ResultatsToolbar
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={resetFilters}
          activeFiltersCount={activeFiltersCount}
          resultsCount={filteredEvents.length}
          totalCount={events.length}
        />

        {loading ? (
          <ResultatsSkeleton />
        ) : filteredEvents.length > 0 ? (
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            {filteredEvents.map((event, index) => (
              <ResultatEventCard
                key={getResultEventKey(event, index)}
                event={event}
                palmaresStatus={palmaresStatus}
                isDeleting={deletingIds.includes(getResultEventKey(event))}
                isAddingPalmares={addingPalmaresIds.includes(
                  getResultEventKey(event),
                )}
                onEdit={() => handleEdit(event)}
                onAddToPalmares={() => handleAddToPalmares(event)}
                onOpenLink={handleOpenLink}
                onImageClick={handleImageClick}
                onDelete={() => handleUnregisterWithConfirm(event)}
              />
            ))}
          </div>
        ) : (
          <ResultatsEmptyState onReset={resetFilters} />
        )}
      </section>

      <EditResultModal
        isOpen={isEditModalOpen}
        event={selectedEvent}
        form={form}
        saving={saving}
        onClose={handleCloseEditModal}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
      />

      <ResultatsImageModal
        imageUrl={selectedImage}
        onClose={() => setSelectedImage(null)}
      />

      <DashboardNavigation />
      <ScrollIndicator />
    </main>
  );
}

export default ResultatsPage;
